import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { filtersParams } from '@/constants/filters.contants';
import { formatDateToYYYYMMDD } from '@/helpers/formatDateTime';
import {
  ColumnFiltersState,
  DateFilterMode,
  FiltersState,
  PaymentMethod,
} from '@/types';

const initialState: FiltersState = {
  filters: {
    page: +filtersParams.Page,
    pagesize: +filtersParams.PageSize,
    sortBy: filtersParams.SortBy,
    sortOrder: filtersParams.SortOrder,
    columnFilters: [],
  },
  currentTab: 'All',
  dateFilterMode: 'All',
};

const filtersSlice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    setActiveTab: (
      state: FiltersState,
      payload: PayloadAction<{ uid: string; gid?: string }>,
    ) => {
      state.filters = { ...initialState.filters };
      state.currentTab = payload.payload.uid;
      state.dateFilterMode = initialState.dateFilterMode;
      if (payload.payload.gid) {
        state.filters.transaction_group_id_exclusive = payload.payload.gid;
        return;
      }

      switch (payload.payload.uid) {
        case 'Warning':
          state.filters.appointment_payment_method = PaymentMethod.unpaid;
          break;
        case 'All':
          break;
        default:
          state.filters.usernameId = payload.payload.uid;
          break;
      }
    },
    setDateRangeFilter: (
      state: FiltersState,
      payload: PayloadAction<{ from: Date; to: Date; mode: DateFilterMode }>,
    ) => {
      const { date_from, date_to, ...filters } = state.filters;
      const from = formatDateToYYYYMMDD(payload.payload.from);
      const to = formatDateToYYYYMMDD(payload.payload.to);
      if (
        (payload.payload.mode !== 'Range' &&
          state.dateFilterMode === payload.payload.mode) ||
        (date_from === from &&
          date_to === to &&
          payload.payload.mode === 'Range')
      ) {
        return;
      }
      state.dateFilterMode = payload.payload.mode;
      state.filters = {
        ...filters,
        transaction_group_id_exclusive: undefined,
        page: +filtersParams.Page,
      };
      if (payload.payload.mode === 'All') return;

      state.filters.date_from = from;
      state.filters.date_to = to;
    },
    setPaymentMethodFilter: (
      state: FiltersState,
      payload: PayloadAction<PaymentMethod | undefined>,
    ) => {
      state.filters.page = +filtersParams.Page;
      state.filters.appointment_payment_method = payload.payload;
      state.filters.transaction_group_id_exclusive = undefined;
    },
    setPagination: (
      state: FiltersState,
      payload: PayloadAction<{ pageIndex: number; pageSize: number }>,
    ) => {
      state.filters.page = payload.payload.pageIndex;
      state.filters.pagesize = payload.payload.pageSize;
    },
    setSearchFilter: (state: FiltersState, payload: PayloadAction<string>) => {
      state.filters.page = +filtersParams.Page;
      state.filters.search = payload.payload;
    },
    setColumnFilters: (
      state: FiltersState,
      payload: PayloadAction<ColumnFiltersState>,
    ) => {
      state.filters.page = +filtersParams.Page;
      state.filters.columnFilters = payload.payload;
    },
  },
});

export const filtersSelector = createSelector(
  (state: { filters: FiltersState }) => state.filters,
  (filters) => filters.filters,
);

export const currentTabSelector = createSelector(
  (state: { filters: FiltersState }) => state.filters,
  (filters) => filters.currentTab,
);

export const dateFilterModeSelector = createSelector(
  (state: { filters: FiltersState }) => state.filters,
  (filters) => filters.dateFilterMode,
);

export const {
  setActiveTab,
  setDateRangeFilter,
  setPaymentMethodFilter,
  setPagination,
  setSearchFilter,
  setColumnFilters,
} = filtersSlice.actions;

export default filtersSlice.reducer;
